import { useEffect, useMemo } from 'react';
import { throttle, debounce } from 'lodash';

export interface UseUserActivityOptions {
  throttleInterval?: number;
  debounceInterval?: number;
  activityEvents?: string[];
  isSessionExpired: boolean;
  trackIframeActivity?: boolean;
}

// Helper function to check if iframe is accessible and ready
const isIframeAccessible = (iframe: HTMLIFrameElement): Document | null => {
  try {
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
    return iframeDocument && iframeDocument.readyState === 'complete' ? iframeDocument : null;
  } catch (error) {
    return null; // Cross-origin or not accessible
  }
};

// Helper function to add event listeners to iframe document
const addIframeEventListeners = (
  iframeDocument: Document,
  events: string[],
  handler: (event: Event) => void,
  cleanupFunctions: (() => void)[]
) => {
  events.forEach(event => {
    iframeDocument.addEventListener(event, handler, { passive: true });
    cleanupFunctions.push(() => {
      try {
        iframeDocument.removeEventListener(event, handler);
      } catch (e) {
        // Iframe may be removed from DOM
      }
    });
  });
};

// Helper function to setup iframe listeners with load handler
const setupIframeWithLoadHandler = (
  iframe: HTMLIFrameElement,
  events: string[],
  handler: (event: Event) => void,
  cleanupFunctions: (() => void)[],
  processedIframes: WeakSet<HTMLIFrameElement>
) => {
  const loadHandler = () => {
    const iframeDocument = isIframeAccessible(iframe);
    if (iframeDocument) {
      processedIframes.add(iframe);
      addIframeEventListeners(iframeDocument, events, handler, cleanupFunctions);
    }
    iframe.removeEventListener('load', loadHandler);
  };
  
  iframe.addEventListener('load', loadHandler);
  cleanupFunctions.push(() => {
    iframe.removeEventListener('load', loadHandler);
  });
};

export const useUserActivity = (
  onActivity: () => void,
  {
    throttleInterval = 3000,
    debounceInterval,
    activityEvents = ['mousedown', 'keydown','click'],
    isSessionExpired,
    trackIframeActivity = true
  }: UseUserActivityOptions
) => {
  // Memoize the events array to prevent unnecessary re-renders
  const stableActivityEvents = useMemo(() => activityEvents, [activityEvents]);

  useEffect(() => {
    if (isSessionExpired) {
      return () => {};
    }

    const activityHandler = debounceInterval
      ? debounce(onActivity, debounceInterval)
      : throttle(onActivity, throttleInterval);

    const handleActivity = (event: Event) => {
      // Trigger activity handler for any event in the activityEvents array
      if (stableActivityEvents.includes(event.type)) {
        activityHandler();
      }
    };

    // Setup window event listeners
    stableActivityEvents.forEach(event => 
      window.addEventListener(event, handleActivity, { passive: true })
    );

    // Setup iframe tracking if enabled
    const iframeCleanupFunctions: (() => void)[] = [];
    const processedIframes = new WeakSet<HTMLIFrameElement>();
    
    if (trackIframeActivity) {
      const processIframe = (iframe: HTMLIFrameElement) => {
        if (processedIframes.has(iframe)) {
          return;
        }
        const iframeDocument = isIframeAccessible(iframe);
        if (iframeDocument) {
          processedIframes.add(iframe);
          addIframeEventListeners(iframeDocument, stableActivityEvents, handleActivity, iframeCleanupFunctions);
        } else {
          // Try again when iframe loads
          setupIframeWithLoadHandler(iframe, stableActivityEvents, handleActivity, iframeCleanupFunctions, processedIframes);
        }
      };

      const addIframeListeners = () => {
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(processIframe);
      };

      // Initial setup
      addIframeListeners();

      // Setup observer to detect new iframes with debounced callback
      const debouncedAddIframeListeners = debounce(addIframeListeners, 100);
      const observer = new MutationObserver(debouncedAddIframeListeners);

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Store observer cleanup
      iframeCleanupFunctions.push(() => {
        observer.disconnect();
        debouncedAddIframeListeners.cancel();
      });
    }

    return () => {
      stableActivityEvents.forEach(event => 
        window.removeEventListener(event, handleActivity)
      );
      iframeCleanupFunctions.forEach(cleanup => cleanup());
      activityHandler.cancel();
    };
  }, [onActivity, throttleInterval, debounceInterval, stableActivityEvents, isSessionExpired, trackIframeActivity]);
};
